import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Truck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Asks who is taking a package before it leaves our chain.
 *
 * Replaces window.prompt, which said "localhost:3000 says", could not be
 * styled, and let you press OK with an empty box — which silently did nothing.
 * Used by the store, central and courier dashboards, so it carries fallbacks
 * for every design token: those are declared per-page with createGlobalStyle
 * and are not present on all three.
 */

const backdropIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// Never from scale(0) — nothing in the real world appears from nothing.
const cardIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(12, 12, 15, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${backdropIn} 200ms var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)) both;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: var(--surface, #ffffff);
  border: 1px solid var(--line, #e2e8f0);
  border-radius: 14px;
  box-shadow: var(--shadow-pop, 0 24px 60px -12px rgba(12, 12, 15, 0.25));
  /* A modal is not anchored to a trigger, so it scales from its own centre. */
  transform-origin: center;
  animation: ${cardIn} var(--dur-modal, 240ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)) both;

  /* Motion sickness: keep the fade, drop the movement. */
  @media (prefers-reduced-motion: reduce) {
    animation: ${backdropIn} 160ms ease both;
  }
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.15rem 1.25rem 0.9rem;

  .icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    flex: none;
    border-radius: 9px;
    background: #ffedd5;
    color: #9a3412;
  }
  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--ink, #0f172a);
  }
  .sub {
    margin-top: 3px;
    font-size: 0.82rem;
    line-height: 1.45;
    color: var(--ink-soft, #64748b);
    /* Package numbers are read digit by digit when someone is checking a label
       against the screen; tabular figures keep them from shifting. */
    font-variant-numeric: tabular-nums;
  }
`;

const CloseButton = styled.button`
  margin-left: auto;
  flex: none;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--ink-soft, #64748b);
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, transform 120ms var(--ease-out, ease-out);

  &:hover { background: var(--surface-2, #f1f5f9); color: var(--ink, #0f172a); }
  &:active { transform: scale(0.94); }
`;

const Body = styled.div`
  padding: 0.35rem 1.25rem 1.15rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ink, #0f172a);
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.6rem 0.7rem;
  font-family: inherit;
  font-size: 0.9rem;
  color: var(--ink, #0f172a);
  background: var(--surface, #fff);
  border: 1.5px solid var(--line, #e2e8f0);
  border-radius: 9px;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &::placeholder { color: #9aa3b2; }
  &:focus {
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15);
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.25rem 1.15rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.85rem;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid var(--line, #e2e8f0);
  background: var(--surface, #fff);
  color: var(--ink, #0f172a);
  /* Named properties only, never the all keyword. */
  transition: transform 120ms var(--ease-out, ease-out), background 160ms ease, opacity 160ms ease;

  &:active { transform: scale(0.97); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  ${(p) => p.$primary && `
    background: #ea580c;
    border-color: #ea580c;
    color: #fff;
    &:hover:not(:disabled) { background: #c2410c; }
  `}
`;

const ThirdPartyHandoffModal = ({ open, packageNumber, busy = false, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const inputRef = useRef(null);

  // Fresh box every time it opens, and the cursor already in it — this is a
  // one-field decision, nobody should have to click first.
  useEffect(() => {
    if (!open) return undefined;
    setName('');
    const id = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const trimmed = name.trim();
  const submit = (event) => {
    event?.preventDefault?.();
    if (!trimmed || busy) return;
    onConfirm?.(trimmed);
  };

  return (
    <Backdrop
      // Clicking the dark area cancels, but only there — a click that started
      // inside the card and drifted out should not throw the typing away.
      onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel?.(); }}
    >
      <Card role="dialog" aria-modal="true" aria-labelledby="handoff-title">
        <Head>
          <div className="icon"><Truck size={17} /></div>
          <div>
            <h2 id="handoff-title">{t('thirdParty.handTo3rdParty')}</h2>
            {/* Just the number. "…is leaving our chain" wrapped to a second
                line to orphan one word, and the warning below already says
                what leaving means. */}
            <div className="sub">{packageNumber || '—'}</div>
          </div>
          <CloseButton type="button" onClick={onCancel} disabled={busy} aria-label={t('thirdParty.cancel')}>
            <X size={15} />
          </CloseButton>
        </Head>

        <form onSubmit={submit}>
          <Body>
            <Label htmlFor="handoff-name">{t('thirdParty.whoIsTakingIt')}</Label>
            <Input
              id="handoff-name"
              ref={inputRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('thirdParty.personSNameOrCompany')}
              autoComplete="off"
              disabled={busy}
            />
          </Body>

          <Footer>
            <Button type="button" onClick={onCancel} disabled={busy}>{t('thirdParty.cancel')}</Button>
            {/* Disabled until there is a name, rather than accepting a blank and
                quietly doing nothing the way the browser prompt did. */}
            <Button type="submit" $primary disabled={!trimmed || busy}>
              <Truck size={14} />
              {busy ? t('thirdParty.handingOver') : t('thirdParty.confirmHandover')}
            </Button>
          </Footer>
        </form>
      </Card>
    </Backdrop>
  );
};

export default ThirdPartyHandoffModal;
